import { useMemo, useEffect, useState, useContext } from "react";
import { ClientService } from "@app/client/clientService";
import { UserContext } from "@app/user/UserContext";
import ChatbotService from "../chatbotService";
import { FeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import Features from "@app/feature-flags/features";
import { AiChatResponse } from "@app/projects/network-assessment/provider-hub/interfaces";

export const UNLIMITED_WEEKLY_QUINN_LIMIT = 2500;

export const LIMIT_REQUEST_INFO_THRESHOLD_PERCENTAGE = 75;

export interface RequestLimitsOptions {
  /** Pre-fetched client limit to use instead of fetching from the API. */
  cachedClientLimit?: number | undefined;
  /** Override the client ID from UserContext for fetching limits. */
  clientIdOverride?: number | undefined;
}

export interface RequestLimits {
  /** The maximum number of weekly prompts allowed for the client. */
  clientLimit: number;
  /** The number of weekly prompts the client has used so far. */
  clientRequests: number;
  /** Whether the client has reached or exceeded their weekly prompt limit. */
  limitReached: boolean;
  /** Whether the limit data is still being fetched. */
  limitsLoading: boolean;
  /** Updates the request count based on an AI chat response; no-op if the response is excluded from prompt limits. */
  processRequestLimits: (response: AiChatResponse) => void;
  showLimitRequestInfo: boolean;
  /** Warning message when the client is approaching or has reached their weekly prompt limit; undefined if no warning threshold met. */
  warningText?: string;
}

export const useRequestLimits = ({ cachedClientLimit, clientIdOverride }: RequestLimitsOptions = { }): RequestLimits => {
  const featuresContext = useContext(FeatureFlagContext);
  const userContext = useContext(UserContext);
  const chatbotService = useMemo(() => new ChatbotService(), []);
  const clientService = useMemo(() => new ClientService(), []);
  const [clientLimit, setClientLimit] = useState<number>(0);
  const [clientRequests, setClientRequests] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);

  const processRequestLimits = (response: AiChatResponse) => {
    if (response.messages.length === 0 || response.messages[0].excludeFromPromptLimits === true) {
      return;
    }
    setClientRequests(prevRequests => prevRequests + 1);
  }

  const getWarningPercentage = (): string | undefined => {
    if (clientLimit === 0) return undefined;
    const percentageUsed = Math.round((clientRequests / clientLimit) * 100);
    if (percentageUsed >= 100) return "100%";
    if (percentageUsed >= 95) return "95%";
    if (percentageUsed >= 90) return "90%";
    if (percentageUsed >= 80) return "80%";
    if (percentageUsed >= 75) return "75%";
    if (percentageUsed >= 50) return "50%";
    if (percentageUsed >= 25) return "25%";
    return undefined;
  };

  const getWarningText = (): string | undefined => {
    const warningPercentage = getWarningPercentage();
    if (warningPercentage === undefined || clientLimit === UNLIMITED_WEEKLY_QUINN_LIMIT) {
      return undefined;
    }
    const text = `Your organization has used ${warningPercentage !== "100%" ? ">" : ""}${warningPercentage} of the allotment of questions for this week.`;
    if (warningPercentage === "100%") {
      return text + " Please try again next week."
    }
    return text;
  }

  const getShowLimitRequestInfo = (): boolean => {
    const warningPercentage = getWarningPercentage();
    if (warningPercentage === undefined || clientLimit === UNLIMITED_WEEKLY_QUINN_LIMIT) return false;
    return parseInt(warningPercentage) >= LIMIT_REQUEST_INFO_THRESHOLD_PERCENTAGE;
  }

  const fetchClientLimit = async () => {
    const activeClientId = clientIdOverride ?? userContext.clientId;
    setLoading(true);
    if (!activeClientId || activeClientId < 0 ||
      (!featuresContext.enabledFeatureFlags.has(Features.AI_VIRTUAL_ASSISTANT) && !featuresContext.enabledFeatureFlags.has(Features.AI_VIRTUAL_ASSISTANT_GA))) {
      setClientLimit(0);
      setClientRequests(0);
      setLoading(false);
      return;
    }
    try {
      if (cachedClientLimit === undefined) {
        const client = await clientService.getClient(activeClientId);
        setClientLimit(client.quinnWeeklyPromptLimit);
      } else {
        setClientLimit(cachedClientLimit);
      }
      const requests = await chatbotService.getWeeklyPromptUsage(activeClientId);
      setClientRequests(requests);
    } catch {
      setClientLimit(cachedClientLimit ?? 0);
      setClientRequests(0);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchClientLimit();
  }, [userContext.clientId, clientIdOverride, cachedClientLimit, featuresContext.enabledFeatureFlags]);

  return {
    clientLimit,
    clientRequests,
    limitReached: !loading && clientRequests >= clientLimit && clientLimit !== UNLIMITED_WEEKLY_QUINN_LIMIT,
    limitsLoading: loading,
    processRequestLimits,
    showLimitRequestInfo: getShowLimitRequestInfo(),
    warningText: getWarningText(),
  };
}